import { updateConfig } from "action/ConfigAction";
import { ContractListResp } from "domain/payload/response/contract/ContractListResp";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ContractService from "service/ContractService";
import HomeService from "service/HomeService";

const ContractPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "expired">("all");
  const [contracts, setContracts] = useState<ContractListResp[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const homeService = HomeService();
  const contractService = ContractService();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filteredContracts = contracts.filter((c) => {
    if (activeFilter === "all") return true;
    if (activeFilter === "active") return c.status.key === 1;
    if (activeFilter === "expired") return c.status.key === 2;
    return true;
  });

  const fetchContracts = async () => {
    try {
      setLoading(true);
      setError(null);

      const status = activeFilter;

      const resp = await contractService.contractList(0, 20, status);
      setContracts(resp?.data || []);
    } catch (err: any) {
      console.error("[fetchContracts] Error:", err);
      setError("Terjadi kesalahan saat memuat kontrak");
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      setIsLoadingConfig(true);
      const configResp = await homeService.getConfig();
      dispatch(updateConfig(configResp.data));
    } catch (e: any) {
      console.error('Failed to load config:', e);
    } finally {
      setIsLoadingConfig(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [activeFilter]);

  const SkeletonContractCard = () => (
    <div className="card mb-3 shadow-sm border-0">
      <div className="card-body d-flex">
        <div className="me-3">
          <div className="skeleton rounded-circle" style={{ width: 40, height: 40 }} />
        </div>

        <div className="flex-grow-1">
          <div className="skeleton" style={{ width: "60%", height: 14, marginBottom: 10 }} />
          <div className="skeleton" style={{ width: "40%", height: 12, marginBottom: 15 }} />

          <div className="d-flex justify-content-between">
            <div className="skeleton" style={{ width: 80, height: 12 }} />
            <div className="skeleton" style={{ width: 140, height: 12 }} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container bg-white rounded-top">
      <div className="d-flex gap-2 mt-3">
        <button
          className={`btn flex-fill ${activeFilter === "all" ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setActiveFilter("all")}
        >
          Semua
        </button>

        <button
          className={`btn flex-fill ${activeFilter === "active" ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setActiveFilter("active")}
        >
          Aktif
        </button>

        <button
          className={`btn flex-fill ${activeFilter === "expired" ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => setActiveFilter("expired")}
        >
          Kadaluarsa
        </button>
      </div>

      <div className="mt-3">
        {loading && (
          <>
            <SkeletonContractCard />
            <SkeletonContractCard />
            <SkeletonContractCard />
          </>
        )}

        {!loading && filteredContracts.length === 0 && (
          <div className="text-center text-muted py-5">
            <h6 className="fw-semibold mb-0">Belum ada kontrak</h6>
          </div>
        )}

        {!loading &&
          filteredContracts.map((contract) => (
            <div
              key={contract.id}
              className="card mb-3 shadow-sm border-0"
              onClick={() => navigate(`/contracts/${contract.id}`)}
              style={{ cursor: "pointer" }}
            >
              <div className="card-body d-flex">
                <div className="me-3">
                  <div
                    className="rounded-circle bg-ps-primary"
                    style={{ width: 40, height: 40 }}
                  ></div>
                </div>

                <div className="flex-grow-1">
                  <h6 className="fw-bold m-0">{contract.name}</h6>
                  <small className="text-muted">{contract.merchantName}</small>
                  <div className="d-flex justify-content-between mt-2">
                    <div>
                      <small className="text-muted">Nilai</small><br />
                      <span className="fw-bold">
                        Rp {contract.fee.toLocaleString("id-ID")}
                      </span>
                    </div>

                    <div className="text-end">
                      <small className="text-muted">Periode</small><br />
                      <span className="fw-bold">
                        {formatDate(contract.startDate)} - {formatDate(contract.endDate)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default ContractPage;